import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdRouteParams } from './useAdRouteParams';
import { useAnalyticalDatasetProfileQuery } from '../api/useAnalyticalDatasetProfileQuery';
import { useProcessAnalyticalDatasetMutation } from '../api/useProcessAnalyticalDatasetMutation';
import { getAnalyticalDatasetVersionPath } from '../routes';
import { selectCohorts, selectHasStagedChanges, selectVariables } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { UseAdInlineProcessResult } from '../types';

// Never pin the button if the new version's status never reports PROCESSING.
const AWAITING_PROCESSING_TIMEOUT_MS = 10_000;

// TODO: cleanup with skip_process_confirmation_modal ff
// Fires AD processing immediately
export const useAdInlineProcess = (): UseAdInlineProcessResult => {
  const navigate = useNavigate();
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  const hasStagedChanges = useAnalyticalDatasetDefinitionStore(selectHasStagedChanges);
  const cohorts = useAnalyticalDatasetDefinitionStore(selectCohorts);
  const variables = useAnalyticalDatasetDefinitionStore(selectVariables);

  const { data: profileEnvelope } = useAnalyticalDatasetProfileQuery({
    projectId,
    analyticalDatasetProfileId,
  });
  const latestStatus = profileEnvelope?.data?.latestStatus;

  const { mutate, isPending, error, reset } = useProcessAnalyticalDatasetMutation();

  // The mutation's onSuccess is a plain promise callback, not gated on this
  // component's lifecycle — if the user navigates away before the request
  // resolves, it still fires and would otherwise force-navigate them back
  // onto this AD's version page. Guard against acting on it post-unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The request resolves (and the page navigates to the new version) before that
  // version's poll reports PROCESSING — during that gap nothing else marks the
  // button in-flight, so it would briefly re-enable. Hold it until the status
  // lands, or the backstop fires.
  const [awaitingProcessingStatus, setAwaitingProcessingStatus] = useState(false);
  useEffect(() => {
    if (!awaitingProcessingStatus) return;
    if (latestStatus === 'PROCESSING' || latestStatus === 'ERROR') {
      setAwaitingProcessingStatus(false);
      return;
    }
    const timer = setTimeout(
      () => setAwaitingProcessingStatus(false),
      AWAITING_PROCESSING_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [awaitingProcessingStatus, latestStatus]);

  const runProcess = useCallback(() => {
    if (!projectId || !analyticalDatasetProfileId || !analyticalDatasetVersionId) return;
    reset();
    mutate(
      {
        projectId,
        analyticalDatasetProfileId,
        analyticalDatasetVersionId,
        ...(hasStagedChanges ? { definition: { cohorts, variables } } : {}),
      },
      {
        onSuccess: (envelope) => {
          if (!isMountedRef.current) return;
          setAwaitingProcessingStatus(true);
          navigate(
            getAnalyticalDatasetVersionPath(
              projectId,
              analyticalDatasetProfileId,
              envelope.data.analyticalDatasetVersionId
            ),
            { replace: true }
          );
        },
      }
    );
  }, [
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
    hasStagedChanges,
    cohorts,
    variables,
    mutate,
    reset,
    navigate,
  ]);

  return {
    runProcess,
    isProcessSubmitting: isPending || awaitingProcessingStatus,
    processError: error ?? null,
  };
};
